import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Heart } from "lucide-react";
import LogoAppe from "../img/header/logoAppe.jpg";
import { navData } from "../data";

const DONATE_URL =
  "https://www.helloasso.com/associations/association-des-professionnels-de-la-petite-enfance/collectes/aider-l-ecole-primaire-a-sarh-au-tchad-fonctionnement-achat-de-tables-bancs";

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openMobileSubmenu, setOpenMobileSubmenu] = useState(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const onResize = () => { if (window.innerWidth >= 1024) setMobileOpen(false); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const handleNavClick = () => {
    setMobileOpen(false);
    setOpenMobileSubmenu(null);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "bg-white/95 backdrop-blur-md shadow-md py-2"
            : "bg-transparent py-4"
        }`}
      >
        <div className="container mx-auto flex items-center justify-between">
          {/* Logo */}
          <a href="/" className="flex items-center gap-3 flex-shrink-0">
            <img
              src={LogoAppe}
              alt="APPE Tchad"
              className="w-10 h-10 rounded-full object-cover border-2 border-secondary/30"
            />
            <div className="hidden sm:block">
              <div
                className={`font-display font-bold text-base tracking-wide leading-tight transition-colors duration-300 ${
                  scrolled ? "text-primary" : "text-white"
                }`}
              >
                APPE Tchad
              </div>
              <div
                className={`text-xs font-body transition-colors duration-300 ${
                  scrolled ? "text-grey" : "text-white/70"
                }`}
              >
                Éduquer pour transformer
              </div>
            </div>
          </a>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navData.items.map((item, i) =>
              item.submenu ? (
                <div
                  key={i}
                  className="relative"
                  onMouseEnter={() => setOpenSubmenu(i)}
                  onMouseLeave={() => setOpenSubmenu(null)}
                >
                  <button
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                      scrolled
                        ? "text-dark hover:text-primary hover:bg-primary/5"
                        : "text-white/90 hover:text-white hover:bg-white/10"
                    }`}
                  >
                    {item.name}
                    <ChevronDown
                      size={14}
                      className={`transition-transform duration-200 ${openSubmenu === i ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence>
                    {openSubmenu === i && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.18 }}
                        className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-card border border-gray-100 overflow-hidden z-50"
                      >
                        {item.submenu.map((sub, j) => (
                          <a
                            key={j}
                            href={sub.href}
                            className="block px-4 py-3 text-sm text-dark hover:bg-primary/5 hover:text-primary transition-colors border-b border-gray-50 last:border-0"
                          >
                            {sub.name}
                          </a>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <a
                  key={i}
                  href={item.href}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    scrolled
                      ? "text-dark hover:text-primary hover:bg-primary/5"
                      : "text-white/90 hover:text-white hover:bg-white/10"
                  }`}
                >
                  {item.name}
                </a>
              )
            )}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center">
            <a
              href={DONATE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 bg-secondary hover:bg-secondary-dark text-white font-semibold text-sm px-6 py-2.5 rounded-full transition-all duration-300 shadow-md hover:shadow-lg hover:-translate-y-0.5"
            >
              <Heart size={14} fill="currentColor" />
              Faire un don
            </a>
          </div>

          {/* Mobile menu toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Menu"
            className={`lg:hidden p-2 rounded-lg transition-colors ${
              scrolled ? "text-dark hover:bg-gray-100" : "text-white hover:bg-white/10"
            }`}
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </motion.header>

      {/* Mobile Navigation Drawer */}
      <AnimatePresence>
        {mobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 lg:hidden"
              onClick={() => setMobileOpen(false)}
            />
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 h-full w-80 max-w-[90vw] bg-white z-50 shadow-2xl flex flex-col lg:hidden"
            >
              {/* Drawer header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <div className="flex items-center gap-3">
                  <img src={LogoAppe} alt="APPE Tchad" className="w-9 h-9 rounded-full object-cover" />
                  <span className="font-display font-bold text-primary text-sm">APPE Tchad</span>
                </div>
                <button
                  onClick={() => setMobileOpen(false)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 text-dark"
                >
                  <X size={20} />
                </button>
              </div>

              {/* Nav links */}
              <nav className="flex-1 overflow-y-auto p-5 space-y-1">
                {navData.items.map((item, i) =>
                  item.submenu ? (
                    <div key={i}>
                      <button
                        onClick={() => setOpenMobileSubmenu(openMobileSubmenu === i ? null : i)}
                        className="w-full flex items-center justify-between px-4 py-3 rounded-xl text-dark hover:bg-primary/5 hover:text-primary font-medium text-sm transition-colors"
                      >
                        {item.name}
                        <ChevronDown
                          size={16}
                          className={`transition-transform ${openMobileSubmenu === i ? "rotate-180" : ""}`}
                        />
                      </button>
                      <AnimatePresence>
                        {openMobileSubmenu === i && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="overflow-hidden ml-4"
                          >
                            {item.submenu.map((sub, j) => (
                              <a
                                key={j}
                                href={sub.href}
                                onClick={handleNavClick}
                                className="block px-4 py-2.5 text-sm text-grey hover:text-primary rounded-lg hover:bg-primary/5 transition-colors"
                              >
                                {sub.name}
                              </a>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  ) : (
                    <a
                      key={i}
                      href={item.href}
                      onClick={handleNavClick}
                      className="block px-4 py-3 rounded-xl text-dark hover:bg-primary/5 hover:text-primary font-medium text-sm transition-colors"
                    >
                      {item.name}
                    </a>
                  )
                )}
              </nav>

              {/* Drawer footer CTA */}
              <div className="p-5 border-t border-gray-100">
                <a
                  href={DONATE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={handleNavClick}
                  className="flex items-center justify-center gap-2 w-full bg-secondary hover:bg-secondary-dark text-white font-semibold py-3.5 rounded-full transition-colors shadow-md"
                >
                  <Heart size={16} fill="currentColor" />
                  Faire un don
                </a>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;
